/**
 * ImportExportService.js
 * Service for handling sheet export and import operations
 * Database-only operations - requires authentication
 */

import { getAllSheets, getSheetById } from './SheetStorageService';
import { isAuthenticated as checkAuth, getAuthToken } from '../utils/AuthUtils';
import logger from './LoggingService';
import { API_URL } from './ApiService';

/**
 * Export a single sheet to a JSON file
 * @param {string} sheetId - ID of the sheet to export
 * @returns {Promise<Object>} Promise that resolves when export is complete
 */
export const exportSingleSheet = async (sheetId) => {
  try {
    // Get the sheet from database
    const sheet = await getSheetById(sheetId);

    if (!sheet) {
      throw new Error(`Sheet with ID ${sheetId} not found`);
    }

    // Export the sheet with database format (_id)
    const jsonData = JSON.stringify(sheet, null, 2);

    // Create a blob and download
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Create a safe filename based on the sheet title
    const safeTitle = sheet.title.replace(/[^a-z0-9]/gi, '_');
    const filename = `${safeTitle}_${sheet._id}.json`;

    // Create a download link and trigger it
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    return { success: true, message: `Sheet "${sheet.title}" exported successfully` };
  } catch (error) {
    logger.error('ImportExportService', 'Error exporting sheet:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export all sheets to a JSON file
 * @returns {Promise<Object>} Promise that resolves when export is complete
 */
export const exportSheets = async () => {
  try {
    // Get all sheets from database
    const sheetList = await getAllSheets(true, true); // sortByNewest=true, skipUIRefresh=true

    if (!sheetList || sheetList.length === 0) {
      throw new Error('No sheets found to export');
    }

    // Fetch the complete data for each sheet
    const completeSheets = [];

    for (const sheetInfo of sheetList) {
      try {
        const completeSheet = await getSheetById(sheetInfo.id);
        if (completeSheet) {
          completeSheets.push(completeSheet);
        }
      } catch (error) {
        logger.error('ImportExportService', `Error processing sheet ${sheetInfo.id}:`, error);
      }
    }

    if (completeSheets.length === 0) {
      throw new Error('Failed to retrieve complete data for any sheets');
    }

    // Create export object with metadata
    const exportData = {
      exportDate: new Date(),
      sheetsCount: completeSheets.length,
      sheets: completeSheets
    };

    // Convert to JSON string
    const jsonData = JSON.stringify(exportData, null, 2);

    // Create blob and download link
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `band_sheets_export_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return {
      success: true,
      message: `${completeSheets.length} sheets exported successfully`
    };
  } catch (error) {
    logger.error('ImportExportService', 'Export failed:', error);
    throw error;
  }
};

/**
 * Import sheets from a JSON file
 * Requires authentication - database only
 * Always creates new sheets (strips _id, backend generates new ones)
 * @param {File} file - The JSON file to import
 * @param {Object} options - Import options (currently unused, for future compatibility)
 * @returns {Promise<Object>} Import results
 */
export const importSheets = async (file, options = {}) => {
  return new Promise((resolve, reject) => {
    // Check authentication first
    const token = getAuthToken();
    const isAuthenticated = checkAuth();

    if (!isAuthenticated || !token) {
      reject(new Error('You must be logged in to import sheets'));
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        // Parse JSON data
        const jsonData = JSON.parse(event.target.result);
        logger.debug('ImportExportService', 'Parsed import file');

        // Determine file format and extract sheets
        let sheetsToImport = [];

        // Case 1: Single sheet format (has _id or id, title, sections)
        if ((jsonData._id || jsonData.id) && jsonData.title && jsonData.sections) {
          sheetsToImport = [jsonData];
          logger.debug('ImportExportService', 'Detected single sheet format');
        }
        // Case 2: Multi-sheet export format (has sheets array)
        else if (jsonData.sheets && Array.isArray(jsonData.sheets) && jsonData.sheets.length > 0) {
          sheetsToImport = jsonData.sheets;
          logger.debug('ImportExportService', 'Detected multi-sheet format with', sheetsToImport.length, 'sheets');
        }
        // Case 3: Unknown format
        else {
          throw new Error('Invalid import file format. Expected single sheet or sheets array.');
        }

        logger.debug('ImportExportService', `Importing ${sheetsToImport.length} sheets to database via API`);

        // Use API import endpoint
        try {
          const response = await fetch(`${API_URL}/import-export/import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ sheets: sheetsToImport })
          });

          if (!response.ok) {
            const errorText = await response.text();
            logger.error('ImportExportService', 'API error:', response.status, errorText);
            throw new Error(`Import failed: ${response.status} - ${response.statusText}`);
          }

          const responseData = await response.json();
          logger.debug('ImportExportService', 'Import successful:', responseData);

          resolve({
            success: true,
            message: responseData.message || `Imported ${responseData.results?.imported || sheetsToImport.length} sheets successfully`,
            results: responseData.results
          });
        } catch (apiError) {
          logger.error('ImportExportService', 'API import failed:', apiError);
          reject(new Error(`Failed to import sheets: ${apiError.message}`));
        }
      } catch (error) {
        logger.error('ImportExportService', 'Error processing import file:', error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};
